import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { api, formatDate } from '../lib/api';
import { telefonoConPrefijo } from '../lib/utils';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  MessageCircle, Search, Send, Loader2, User, Check, CheckCheck, PlugZap, CornerUpLeft,
} from 'lucide-react';
import AvatarIniciales from '../components/AvatarIniciales';

/**
 * Bandeja de WhatsApp (opción B): el número de la clínica vive en la API de
 * Meta y el equipo lee y contesta desde aquí. Hasta que el número esté
 * migrado, la página funciona en modo "pendiente de conectar".
 */

function hora(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch { return ''; }
}

function TicksEstado({ m }) {
  if (m.dir !== 'out') return null;
  if (m.status === 'read') return <CheckCheck className="w-3.5 h-3.5 text-sky-400" />;
  if (m.status === 'delivered') return <CheckCheck className="w-3.5 h-3.5 opacity-60" />;
  return <Check className="w-3.5 h-3.5 opacity-60" />;
}

export default function WhatsAppPage() {
  const [conectado, setConectado] = useState(null); // null = comprobando
  const [convs, setConvs] = useState([]);
  const [q, setQ] = useState('');
  const [telActivo, setTelActivo] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const finRef = useRef(null);

  useEffect(() => {
    api.get('/api/whatsapp/estado')
      .then((r) => setConectado(!!r.data?.conectado))
      .catch(() => setConectado(false));
  }, []);

  const cargarConvs = useCallback(() => {
    const params = q.trim() ? { q: q.trim() } : {};
    api.get('/api/whatsapp/conversaciones', { params })
      .then((r) => setConvs(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, [q]);

  const cargarMensajes = useCallback((tel) => {
    if (!tel) return;
    api.get(`/api/whatsapp/conversaciones/${tel}/mensajes`)
      .then((r) => {
        setMensajes(Array.isArray(r.data) ? r.data : []);
        setTimeout(() => finRef.current?.scrollIntoView({ behavior: 'auto' }), 50);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { const t = setTimeout(cargarConvs, q ? 300 : 0); return () => clearTimeout(t); }, [cargarConvs, q]);
  useEffect(() => { cargarMensajes(telActivo); }, [telActivo, cargarMensajes]);

  // La bandeja se refresca sola, como la agenda: los WhatsApps llegan cuando
  // llegan. Cada 15 s si la pestaña está a la vista.
  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      cargarConvs();
      if (telActivo) cargarMensajes(telActivo);
    };
    const id = setInterval(tick, 15000);
    window.addEventListener('focus', tick);
    return () => { clearInterval(id); window.removeEventListener('focus', tick); };
  }, [cargarConvs, cargarMensajes, telActivo]);

  const enviar = async () => {
    const cuerpo = texto.trim();
    if (!cuerpo || !telActivo) return;
    setEnviando(true);
    try {
      await api.post(`/api/whatsapp/conversaciones/${telActivo}/enviar`, { texto: cuerpo });
      setTexto('');
      cargarMensajes(telActivo);
      cargarConvs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo enviar');
    } finally {
      setEnviando(false);
    }
  };

  const convActiva = convs.find((c) => c.tel === telActivo);

  return (
    <div>
      <PageHeader
        title="WhatsApp"
        subtitle="Conversaciones con pacientes por el número de la clínica."
      />

      {conectado === false && (
        <div className="cp-card p-4 mb-4 border-l-4 border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20 flex items-start gap-3" data-testid="wa-sin-conectar">
          <PlugZap className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-semibold text-[#13231E]">Pendiente de conectar con Meta</div>
            <div className="text-muted-foreground">
              La bandeja está lista, pero falta migrar el número de la clínica a la API
              oficial de WhatsApp Business y poner las credenciales en el servidor.
              Cuando esté hecho, este aviso desaparece y los mensajes entran solos.
            </div>
          </div>
        </div>
      )}

      <div className="cp-card overflow-hidden grid grid-cols-1 md:grid-cols-[320px_1fr]" style={{ minHeight: 480 }}>
        {/* Lista de conversaciones */}
        <div className="border-r border-border flex flex-col">
          <div className="p-2.5 border-b border-border">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="pl-8 h-9" data-testid="wa-buscar" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-1.5 space-y-0.5">
            {convs.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                {conectado === false ? 'Sin conversaciones aún.' : 'Sin conversaciones.'}
              </div>
            ) : convs.map((c) => {
              // Sin ficha, el número ES el nombre de la fila: con más razón se lee
              // con sus huecos.
              const quien = c.patient_name || telefonoConPrefijo(`+${c.tel}`);
              const activa = telActivo === c.tel;
              return (
                <button
                  key={c.tel}
                  type="button"
                  onClick={() => setTelActivo(c.tel)}
                  aria-current={activa ? 'true' : undefined}
                  className={`w-full text-left px-2 py-2 rounded-xl flex items-start gap-2.5
                    transition-colors duration-150
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2E6E5E]
                    ${activa ? 'bg-[rgba(46,110,94,0.10)]' : 'hover:bg-muted/50'}`}
                  data-testid="wa-conversacion"
                >
                  {/* Avatar de color estable: antes eran todas las filas iguales
                      y había que leer para encontrar a alguien. */}
                  <AvatarIniciales nombre={quien} size={38} />
                  <span className="flex-1 min-w-0">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className={`truncate ${c.unread > 0 ? 'font-semibold text-[#0B1220]' : 'font-medium text-[#13231E]'}`}>
                        {quien}
                      </span>
                      <span className="text-[11px] text-muted-foreground shrink-0">{hora(c.last_at)}</span>
                    </span>
                    <span className="flex items-center justify-between gap-2 mt-0.5">
                      <span className={`text-xs truncate ${c.unread > 0 ? 'text-[#13231E]' : 'text-muted-foreground'}`}>
                        {c.last_dir === 'out' && <CornerUpLeft className="w-3 h-3 inline mr-1 -mt-px" />}
                        {c.last_text}
                      </span>
                      {c.unread > 0 && (
                        <span className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-[#2E6E5E] text-white text-[11px] font-bold flex items-center justify-center">
                          {c.unread}
                        </span>
                      )}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Hilo */}
        <div className="flex flex-col">
          {!telActivo ? (
            <div className="flex-1 flex items-center justify-center">
              <EmptyState icon={MessageCircle} title="Elige una conversación" description="Los mensajes de los pacientes aparecerán a la izquierda." />
            </div>
          ) : (
            <>
              <div className="px-4 py-2.5 border-b border-border flex items-center justify-between gap-2">
                <div className="min-w-0 flex items-center gap-3">
                  <AvatarIniciales nombre={convActiva?.patient_name || `+${telActivo}`} size={36} />
                  <div className="min-w-0">
                    <div className="font-semibold text-[#13231E] truncate">{convActiva?.patient_name || `+${telActivo}`}</div>
                    <div className="text-xs text-muted-foreground">+{telActivo}</div>
                  </div>
                </div>
                {convActiva?.patient_id && (
                  <Button size="sm" variant="outline" asChild data-testid="wa-ver-ficha">
                    <Link to={`/pacientes/${convActiva.patient_id}`} target="_blank" rel="noopener">
                      <User className="w-3.5 h-3.5 mr-1.5" /> Ver ficha
                    </Link>
                  </Button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[rgba(46,110,94,0.02)]">
                {/* Burbujas: esquina "pegada" al lado de quien habla y sin
                    sombra (2026-07-27). La sombra y las cuatro esquinas iguales
                    era lo que más las envejecía. */}
                {mensajes.map((m) => {
                  const mia = m.dir === 'out';
                  return (
                    <div key={m.id} className={`flex ${mia ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[78%] px-3 py-2 text-sm ${
                        mia
                          ? 'bg-[#2E6E5E] text-white rounded-2xl rounded-br-md'
                          : 'bg-white dark:bg-card ring-1 ring-border text-[#13231E] rounded-2xl rounded-bl-md'
                      }`}>
                        <div className="whitespace-pre-wrap break-words">{m.texto}</div>
                        <div className={`flex items-center gap-1 justify-end text-[11px] mt-1 ${mia ? 'text-white/70' : 'text-muted-foreground'}`}>
                          {mia && m.sent_by_name ? `${m.sent_by_name} · ` : ''}
                          {formatDate((m.created_at || '').slice(0, 10))} {hora(m.created_at)}
                          <TicksEstado m={m} />
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={finRef} />
              </div>
              <div className="p-3 border-t border-border flex items-end gap-2">
                <Textarea
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
                  placeholder={conectado ? 'Escribe un mensaje… (Enter envía)' : 'Pendiente de conectar con Meta'}
                  disabled={!conectado}
                  className="min-h-[42px] max-h-32 resize-none"
                  data-testid="wa-texto"
                />
                <Button onClick={enviar} disabled={!conectado || enviando || !texto.trim()} className="bg-[#2E6E5E] hover:bg-[#1E4A3F] h-[42px]" data-testid="wa-enviar">
                  {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
